import { Component, DestroyRef, effect, inject, Injector, input, output, Signal, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CardLoaderModule, DeviceDetailsModule, SiteDetailsModule } from '@iot-platform/iot-platform-ui';
// TODO: should not use iot4bos ui component as it creates a circular dependency
import { BasicInfoComponent } from '../../../../../../iot4bos/iot4bos-ui-asset/src/lib/components/basic-info/basic-info.component';
import { FollowedVariablesDetailsComponent } from '../../../../../../iot4bos/iot4bos-ui-asset/src/lib/components/followed-variables-details/followed-variables-details.component';
import { Contact, TagCategory } from '@iot-platform/models/common';
import { Asset, Device, Site } from '@iot-platform/models/i4b';
import { Layer } from 'leaflet';
import moment from 'moment';
import { finalize } from 'rxjs/operators';
import { MapPanelInfoPopupComponent } from '../map-panel-info-popup/map-panel-info-popup.component';
import { MapPanelInfoToolboxComponent } from '../map-panel-info-toolbox/map-panel-info-toolbox.component';
import { IotMapFacade, MapPanelInfoService } from '@iot-platform/data-access/maps';
import { IotGeoJsonFeature, MapNavigationEvent, MapNavigationModelType } from '@iot-platform/models/maps';

@Component({
  imports: [
    NgTemplateOutlet,
    BasicInfoComponent,
    MapPanelInfoToolboxComponent,
    FollowedVariablesDetailsComponent,
    MapPanelInfoPopupComponent,
    CardLoaderModule,
    SiteDetailsModule,
    DeviceDetailsModule,
    FlexLayoutModule,
    MatIconModule,
    MatButtonModule
  ],
  selector: 'iot-platform-maps-map-panel-info',
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './map-panel-info.component.html'
})
export class MapPanelInfoComponent {
  private readonly mapPanelInfoService: MapPanelInfoService = inject(MapPanelInfoService);
  private readonly mapFacade: IotMapFacade = inject(IotMapFacade);
  private readonly injector: Injector = inject(Injector);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  feature = input<IotGeoJsonFeature>();

  // eslint-disable-next-line @angular-eslint/no-output-native
  close = output<boolean>();
  selectElement = output<MapNavigationEvent<MapNavigationModelType>>();
  displayRoute = output<{ asset: Asset; daysToDisplay: string }>();
  displaySegments = output<{ layers: Layer[]; action: 'add' | 'remove' }>();

  site: WritableSignal<Site | null> = signal(null);
  asset: WritableSignal<Asset | null> = signal(null);
  device: WritableSignal<Device | null> = signal(null);
  tags: WritableSignal<TagCategory[]> = signal([]);
  assetsBySite: WritableSignal<Asset[]> = signal([]);
  devicesBySite: WritableSignal<Device[]> = signal([]);
  contacts: WritableSignal<Contact[]> = signal([]);
  complementarySite: WritableSignal<Site | null> = signal(null);
  loading: WritableSignal<boolean> = signal(false);
  concept: WritableSignal<string | null> = signal(null);
  canReadContacts: Signal<boolean> = this.mapFacade.canReadContacts;

  featureChange = effect(
    () => {
      const feature = this.feature();
      const concept = feature?.properties?.['concept'];
      const id = feature?.properties?.['id'];
      this.concept.set(concept);

      if (concept === 'site') {
        this.initSiteInfo(id as string);
      } else if (concept === 'asset') {
        this.initAssetInfo(id as string);
      } else if (concept === 'device') {
        this.initDeviceInfo(id as string);
      }
    },
    { injector: this.injector }
  );

  loadRoutes(event: { asset: Asset; daysToDisplay: string; minutesBetweenSegment: number; limit: number }) {
    const now = new Date();
    const startDate = moment(now).subtract(event.daysToDisplay, 'minutes').toDate();
    this.mapFacade.loadRoute(event.asset?.id as string, event.limit, startDate, now, event.minutesBetweenSegment, 'desc');
  }

  closeSidebar() {
    this.mapFacade.clearRoutes();
    this.close.emit(true);
  }

  private initSiteInfo(id: string) {
    this.loading.set(true);
    this.mapPanelInfoService
      .getSite(id)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([site, tags, assets, devices, contacts]: [Site, TagCategory[], Asset[], Device[], Contact[]]) => {
        this.site.set(site);
        this.tags.set(tags);
        this.assetsBySite.set(assets);
        this.devicesBySite.set(devices);
        this.contacts.set(contacts);
      });
  }

  private initAssetInfo(id: string) {
    this.loading.set(true);
    this.mapPanelInfoService
      .getAsset(id)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([asset, site, tags]: [Asset, Site, TagCategory[]]) => {
        this.asset.set(asset);
        this.complementarySite.set(site);
        this.tags.set(tags);
      });
  }

  private initDeviceInfo(id: string) {
    this.loading.set(true);
    this.mapPanelInfoService
      .getDevice(id)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([device, site, tags]: [Device, Site, TagCategory[]]) => {
        this.device.set(device);
        this.complementarySite.set(site);
        this.tags.set(tags);
      });
  }
}
